import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, getUser } from "@/lib/api";
import { useRegion } from "@/lib/region";
import RegionToggle from "@/components/RegionToggle";
import { toast } from "sonner";
import { CalendarBlank, MapPin, ArrowRight, Note, Phone, CreditCard, Handshake, DeviceMobile, Gift, UserPlus } from "@phosphor-icons/react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { pushRecent } from "@/lib/recentViews";
import { saveDraft, clearDraft, getDraft } from "@/lib/bookingDraft";
import WhatsAppBookingButton from "@/components/WhatsAppBookingButton";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";

export default function BookingPage() {
  const { vertical, lookupKey } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { region, meta } = useRegion();
  const [pkg, setPkg] = useState(null);
  const [vname, setVname] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState(searchParams.get("notes") || "");
  const [paymentMode, setPaymentMode] = useState(meta.defaultPaymentMode);
  const [loading, setLoading] = useState(false);
  const [gateway, setGateway] = useState("stripe"); // stripe | paypal
  // Mobile Money (Liberia only)
  const [momoProvider, setMomoProvider] = useState("orange"); // orange | mtn | lonestar
  const [momoMsisdn, setMomoMsisdn] = useState("");
  // Gift a service (diaspora → Liberia use-case)
  const [isGift, setIsGift] = useState(searchParams.get("gift") === "1");
  const [giftRecipient, setGiftRecipient] = useState(searchParams.get("r_name") || "");
  const [giftPhone, setGiftPhone] = useState(searchParams.get("r_phone") || "");
  const [giftMessage, setGiftMessage] = useState(searchParams.get("r_msg") || "");

  // If gift is toggled ON and the user was on Mobile Money, force them back to Stripe.
  useEffect(() => {
    if (isGift && paymentMode === "mobile_money") {
      setPaymentMode("online");
      setGateway("stripe");
    }
  }, [isGift, paymentMode]);

  useEffect(() => {
    api.get(`/catalog/${vertical}`).then(res => {
      const found = res.data.packages.find(p => p.lookup_key === lookupKey);
      setPkg(found);
      setVname(res.data.name);
      if (found) {
        pushRecent({
          vertical,
          lookup_key: found.lookup_key,
          name: found.name,
          amount: found.amount,
          region,
        });
      }
    });
  }, [vertical, lookupKey, region]);

  // Sync payment mode when region changes
  useEffect(() => {
    setPaymentMode(meta.defaultPaymentMode);
  }, [region, meta.defaultPaymentMode]);

  useEffect(() => {
    // If the visitor left a matching draft, restore it on this booking page.
    const draft = getDraft();
    if (draft && draft.vertical === vertical && draft.lookup_key === lookupKey) {
      if (draft.address) setAddress(draft.address);
      if (draft.notes) setNotes(draft.notes);
      if (draft.scheduledAt) {
        setScheduledAt(draft.scheduledAt);
        toast.success("Restored your draft booking");
        return;
      }
    }
    const t = new Date();
    t.setDate(t.getDate() + 1);
    t.setHours(10, 0, 0, 0);
    setScheduledAt(t.toISOString().slice(0, 16));
  }, [vertical, lookupKey]);

  // Persist a lightweight draft while the visitor fills in the form so we can offer "Continue booking" later.
  useEffect(() => {
    if (!pkg) return;
    // Only save when the user has typed *something* meaningful (address non-empty).
    if (!address.trim()) return;
    saveDraft({
      vertical,
      lookup_key: lookupKey,
      name: pkg.name,
      region,
      scheduledAt,
      address,
      notes,
    });
  }, [pkg, vertical, lookupKey, region, scheduledAt, address, notes]);

  const override = pkg?.region_overrides?.[region];
  const localPrice = pkg
    ? (override?.amount != null ? override.amount / 100 : (pkg.amount * meta.priceMultiplier) / 100)
    : 0;
  const displayDescription = override?.description || pkg?.description;
  const perks = override?.perks || [];

  const submit = async (e) => {
    e.preventDefault();
    // PayPal path is handled by its own buttons — the main form submit is Stripe-only or Reserve.
    if (paymentMode === "online" && gateway === "paypal") return;
    const user = getUser();
    if (!user) {
      toast.error("Please sign in to book");
      navigate("/login?next=" + encodeURIComponent(window.location.pathname));
      return;
    }
    if (!address.trim()) {
      toast.error("Address required");
      return;
    }
    if (isGift) {
      if (!giftRecipient.trim() || !giftPhone.trim()) {
        toast.error("Recipient name and phone are required for gifts");
        return;
      }
      if (paymentMode === "mobile_money" || paymentMode === "reserve") {
        toast.error("Gifts must be paid online (Stripe or PayPal)");
        return;
      }
    }
    if (paymentMode === "mobile_money") {
      if (!momoMsisdn.trim() || momoMsisdn.trim().length < 6) {
        toast.error("Enter your mobile number");
        return;
      }
    }
    setLoading(true);
    try {
      const res = await api.post("/bookings/checkout", {
        vertical,
        lookup_key: lookupKey,
        scheduled_at: new Date(scheduledAt).toISOString(),
        address,
        notes,
        origin_url: window.location.origin,
        region,
        payment_mode: paymentMode,
        mobile_provider: paymentMode === "mobile_money" ? momoProvider : null,
        mobile_msisdn: paymentMode === "mobile_money" ? momoMsisdn.trim() : null,
        is_gift: isGift,
        gift_recipient_name: isGift ? giftRecipient.trim() : null,
        gift_recipient_phone: isGift ? giftPhone.trim() : null,
        gift_message: isGift ? giftMessage.trim() || null : null,
      });
      if (res.data.mode === "online") {
        clearDraft();
        window.location.href = res.data.checkout_url;
      } else if (res.data.mode === "mobile_money") {
        clearDraft();
        navigate(`/reservation/${res.data.reservation_ref}`, { state: { ...res.data, momoProvider, momoMsisdn } });
      } else {
        clearDraft();
        navigate(`/reservation/${res.data.reservation_ref}`, { state: res.data });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed");
      setLoading(false);
    }
  };

  if (!pkg) return <div className="max-w-4xl mx-auto p-10 text-zinc-400">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16" data-testid="booking-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="eyebrow mb-4">{vname}</div>
          <h1 className="font-serif-display text-4xl lg:text-5xl text-white">Book · {pkg.name}</h1>
        </div>
        <div className="flex flex-col items-end gap-3">
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-400">Booking region</div>
          <RegionToggle />
        </div>
      </div>

      <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-sm text-[#D4AF37]">
        <span>{meta.flag}</span>
        <span>{meta.label} booking · dispatched by {meta.dispatcherName}</span>
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-10">
        <form onSubmit={submit} className="lg:col-span-3 space-y-6">
          <div>
            <label className="eyebrow block mb-3">Scheduled Date &amp; Time</label>
            <div className="relative">
              <CalendarBlank className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                data-testid="booking-datetime-input"
                required
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-[#121214] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none transition"
              />
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-3">Service Address</label>
            <div className="relative">
              <MapPin className="absolute left-4 top-4 text-zinc-500" size={18} />
              <textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                data-testid="booking-address-input"
                required
                rows={3}
                placeholder={region === "liberia" ? "Community, town, county (e.g., Marshall, Margibi)" : "Street, city, ZIP"}
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-[#121214] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none transition"
              />
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-3">Notes (optional)</label>
            <div className="relative">
              <Note className="absolute left-4 top-4 text-zinc-500" size={18} />
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="booking-notes-input"
                rows={3}
                placeholder="Any special instructions?"
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-[#121214] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none transition"
              />
            </div>
          </div>

          {/* Gift a Service toggle */}
          <div>
            <button
              type="button"
              onClick={() => setIsGift(v => !v)}
              data-testid="gift-toggle-btn"
              data-sound="coin"
              className={`w-full flex items-center gap-4 p-4 rounded-xl border text-left transition ${
                isGift ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/30"
              }`}
            >
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${isGift ? "bg-[#D4AF37] text-black" : "bg-white/5 text-[#D4AF37]"}`}>
                <Gift weight={isGift ? "fill" : "duotone"} size={20} />
              </div>
              <div className="flex-1">
                <div className="text-white font-medium text-sm">Gift this service to someone in {meta.label}</div>
                <div className="text-xs text-zinc-500 mt-0.5">Diaspora favourite · we handle recipient outreach on your behalf.</div>
              </div>
              <div className={`w-11 h-6 rounded-full relative transition ${isGift ? "bg-[#D4AF37]" : "bg-white/10"}`}>
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition ${isGift ? "right-0.5" : "left-0.5"}`} />
              </div>
            </button>

            {isGift && (
              <div data-testid="gift-fields" className="mt-4 space-y-4 rounded-xl border border-[#D4AF37]/30 bg-black/30 p-5">
                <div>
                  <label className="eyebrow block mb-2 flex items-center gap-2"><UserPlus size={12} weight="duotone" /> Recipient full name</label>
                  <input
                    value={giftRecipient}
                    onChange={e => setGiftRecipient(e.target.value)}
                    data-testid="gift-recipient-name"
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white outline-none focus:border-[#D4AF37]"
                    placeholder="e.g. Mama Grace"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Recipient phone / WhatsApp</label>
                  <input
                    value={giftPhone}
                    onChange={e => setGiftPhone(e.target.value)}
                    data-testid="gift-recipient-phone"
                    type="tel"
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white outline-none focus:border-[#D4AF37]"
                    placeholder="+231 77 000 0000"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Personal note <span className="text-zinc-600 lowercase">(optional)</span></label>
                  <textarea
                    value={giftMessage}
                    onChange={e => setGiftMessage(e.target.value)}
                    data-testid="gift-message"
                    rows={2}
                    maxLength={200}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white outline-none focus:border-[#D4AF37]"
                    placeholder="From your loving son in Newark ✨"
                  />
                  <div className="text-[10px] text-zinc-600 mt-1 text-right">{giftMessage.length}/200</div>
                </div>
                <div className="text-xs text-zinc-500 leading-relaxed">
                  Gifts are paid online with card or PayPal (USD). {meta.dispatcherName} will contact the recipient within 24 hours to confirm timing.
                </div>
              </div>
            )}
          </div>

          {/* Payment mode selector */}
          <div>
            <label className="eyebrow block mb-3">Payment method</label>
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${region === "liberia" && !isGift ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-3`}>
              <button
                type="button"
                onClick={() => { setPaymentMode("online"); setGateway("stripe"); }}
                data-testid="paymode-stripe-btn"
                data-sound="tock"
                className={`p-4 rounded-xl border text-left transition ${paymentMode === "online" && gateway === "stripe" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/30"}`}
              >
                <CreditCard weight="duotone" size={20} className="text-[#D4AF37] mb-2" />
                <div className="text-white font-medium text-sm">Card · Stripe</div>
                <div className="text-xs text-zinc-500 mt-1">Visa, Mastercard, Amex</div>
              </button>
              <button
                type="button"
                onClick={() => { setPaymentMode("online"); setGateway("paypal"); }}
                data-testid="paymode-paypal-btn"
                data-sound="tock"
                className={`p-4 rounded-xl border text-left transition ${paymentMode === "online" && gateway === "paypal" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/30"}`}
              >
                <div className="mb-2 font-bold text-[15px] tracking-tight">
                  <span className="text-[#003087]">Pay</span><span className="text-[#009cde]">Pal</span>
                </div>
                <div className="text-white font-medium text-sm">PayPal</div>
                <div className="text-xs text-zinc-500 mt-1">Balance, card or bank</div>
              </button>
              {region === "liberia" && !isGift && (
                <button
                  type="button"
                  onClick={() => setPaymentMode("mobile_money")}
                  data-testid="paymode-momo-btn"
                  data-sound="tock"
                  className={`p-4 rounded-xl border text-left transition ${paymentMode === "mobile_money" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/30"}`}
                >
                  <DeviceMobile weight="duotone" size={20} className="text-[#D4AF37] mb-2" />
                  <div className="text-white font-medium text-sm">Mobile Money</div>
                  <div className="text-xs text-zinc-500 mt-1">Orange · MTN · Lonestar</div>
                </button>
              )}
              {!isGift && (
                <button
                  type="button"
                  onClick={() => setPaymentMode("reserve")}
                  data-testid="paymode-reserve-btn"
                  data-sound="tock"
                  className={`p-4 rounded-xl border text-left transition ${paymentMode === "reserve" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/30"}`}
                >
                  <Handshake weight="duotone" size={20} className="text-[#D4AF37] mb-2" />
                  <div className="text-white font-medium text-sm">Reserve · Pay on arrival</div>
                  <div className="text-xs text-zinc-500 mt-1">Confirm with {meta.dispatcherName}</div>
                </button>
              )}
            </div>
          </div>

          {paymentMode === "mobile_money" && (
            <MobileMoneyBlock
              provider={momoProvider}
              setProvider={setMomoProvider}
              msisdn={momoMsisdn}
              setMsisdn={setMomoMsisdn}
            />
          )}

          {paymentMode === "online" && gateway === "paypal" ? (
            <PaypalCheckoutBlock
              vertical={vertical}
              lookupKey={lookupKey}
              scheduledAt={scheduledAt}
              address={address}
              notes={notes}
              region={region}
              navigate={navigate}
            />
          ) : (
            <button
              type="submit"
              disabled={loading}
              data-testid="booking-submit-btn"
              data-sound="coin"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#D4AF37] text-black font-semibold text-sm hover:bg-[#F3E5AB] transition-colors disabled:opacity-60"
            >
              {loading
                ? "Processing…"
                : isGift
                ? "Send gift & pay securely"
                : paymentMode === "online"
                ? "Continue to secure checkout"
                : paymentMode === "mobile_money"
                ? "Confirm & get payment code"
                : "Confirm reservation"} <ArrowRight size={16} weight="bold" />
            </button>
          )}
        </form>

        <aside className="lg:col-span-2">
          <div className="sticky top-28 rounded-2xl border border-white/10 bg-[#121214] p-8">
            <div className="eyebrow mb-2">Order Summary</div>
            <div className="font-serif-display text-2xl text-white mb-1">{pkg.name}</div>
            <div className="text-sm text-zinc-400">{displayDescription}</div>

            {perks.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {perks.map(p => (
                  <li key={p} className="text-xs text-zinc-300 flex items-start gap-2">
                    <span className="text-[#D4AF37] mt-[3px]">◆</span> {p}
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs bg-white/5 border border-white/10">
              <span>{meta.flag}</span>
              <span className="text-zinc-300">{meta.label} region</span>
            </div>

            <div className="h-px bg-white/10 my-6" />
            <div className="flex justify-between text-sm text-zinc-400">
              <span>Base price ({meta.label})</span>
              <span>${localPrice.toFixed(2)} USD</span>
            </div>
            <div className="flex justify-between text-sm text-zinc-400 mt-2">
              <span>Duration</span>
              <span>{pkg.duration_min} min</span>
            </div>
            <div className="h-px bg-white/10 my-6" />
            <div className="flex justify-between items-baseline">
              <span className="text-zinc-300">Total</span>
              <span className="font-serif-display text-3xl text-white">${localPrice.toFixed(2)}</span>
            </div>

            <div className="mt-6 rounded-xl border border-white/10 bg-black/40 p-4">
              <div className="eyebrow gold-text mb-1">Your dispatcher</div>
              <div className="text-white font-medium">{meta.dispatcherName}</div>
              <a href={`tel:${meta.dispatcherPhoneHref}`} className="mt-1 flex items-center gap-2 text-sm text-zinc-300 hover:text-[#D4AF37] transition">
                <Phone size={14} weight="duotone" /> {meta.dispatcherPhone}
              </a>
              <div className="mt-3">
                <WhatsAppBookingButton
                  dispatcherName={meta.dispatcherName}
                  dispatcherPhone={meta.dispatcherPhone}
                  subject={`Hi ${meta.dispatcherName}, I'd like help with a RideXelite ${pkg.name} booking`}
                  lines={[
                    `Service: ${pkg.name} (${vname})`,
                    `Region: ${meta.label}`,
                    scheduledAt ? `Scheduled: ${new Date(scheduledAt).toLocaleString()}` : "",
                    address ? `Address: ${address}` : "",
                    notes ? `Notes: ${notes}` : "",
                    isGift && giftRecipient ? `Gift recipient: ${giftRecipient}${giftPhone ? ` (${giftPhone})` : ""}` : "",
                  ]}
                  testId="booking-whatsapp-btn"
                />
              </div>
            </div>

            <p className="mt-6 text-xs text-zinc-500 leading-relaxed">
              {paymentMode === "online" ? (
                gateway === "paypal" ? (
                  <>Secure PayPal checkout. Pay with your PayPal balance, card or bank.</>
                ) : (
                  <>Secure Stripe checkout. Test card: <span className="text-zinc-300">4242 4242 4242 4242</span>.</>
                )
              ) : paymentMode === "mobile_money" ? (
                <>Send the amount via Orange, MTN, or Lonestar Mobile Money to the dispatcher, quoting your reservation code. We confirm within minutes.</>
              ) : (
                <>Your reservation will be confirmed instantly. Pay on arrival directly to your provider or dispatcher.</>
              )}
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}


function PaypalCheckoutBlock({ vertical, lookupKey, scheduledAt, address, notes, region, navigate }) {
  if (!PAYPAL_CLIENT_ID) {
    return (
      <div
        data-testid="paypal-not-configured"
        className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200"
      >
        PayPal is not configured yet. Please add your <code className="text-amber-100">REACT_APP_PAYPAL_CLIENT_ID</code> to the frontend .env and restart the frontend service.
      </div>
    );
  }

  const validate = () => {
    const user = getUser();
    if (!user) {
      toast.error("Please sign in to book");
      navigate("/login?next=" + encodeURIComponent(window.location.pathname));
      return false;
    }
    if (!address.trim()) {
      toast.error("Address required");
      return false;
    }
    return true;
  };

  return (
    <div data-testid="paypal-buttons-block" className="rounded-xl border border-white/10 bg-black/30 p-5">
      <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">Complete payment via PayPal</div>
      <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD", intent: "capture" }}>
        <PayPalButtons
          style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal" }}
          createOrder={async () => {
            if (!validate()) throw new Error("validation");
            try {
              const res = await api.post("/paypal/create-order", {
                vertical,
                lookup_key: lookupKey,
                scheduled_at: new Date(scheduledAt).toISOString(),
                address,
                notes,
                origin_url: window.location.origin,
                region,
                payment_mode: "online",
              });
              return res.data.order_id;
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Could not start PayPal checkout");
              throw err;
            }
          }}
          onApprove={async (data) => {
            try {
              const res = await api.post(`/paypal/capture-order/${data.orderID}`);
              clearDraft();
              toast.success("Payment received");
              navigate(`/reservation/${res.data.reservation_ref}`, { state: { ...res.data, mode: "online", paid: true } });
            } catch (err) {
              toast.error(err?.response?.data?.detail || "PayPal capture failed");
            }
          }}
          onError={(err) => {
            console.error("PayPal error", err);
            toast.error("PayPal error — please try again");
          }}
          onCancel={() => toast("Payment cancelled")}
        />
      </PayPalScriptProvider>
    </div>
  );
}

const MOMO_PROVIDERS = [
  { id: "orange", label: "Orange Money", accent: "#FF6600" },
  { id: "mtn",    label: "MTN Mobile Money", accent: "#FFCC00" },
  { id: "lonestar", label: "Lonestar Cell MoMo", accent: "#0057B7" },
];

function MobileMoneyBlock({ provider, setProvider, msisdn, setMsisdn }) {
  return (
    <div data-testid="momo-block" className="rounded-xl border border-white/10 bg-black/30 p-5 space-y-4">
      <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Choose your Mobile Money provider</div>
      <div className="grid grid-cols-3 gap-2">
        {MOMO_PROVIDERS.map(p => (
          <button
            key={p.id}
            type="button"
            onClick={() => setProvider(p.id)}
            data-testid={`momo-provider-${p.id}`}
            className={`px-2 py-3 rounded-lg border text-center text-xs font-medium transition ${
              provider === p.id ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/30"
            }`}
          >
            <span className="block w-3 h-3 rounded-full mx-auto mb-2" style={{ background: p.accent, boxShadow: `0 0 8px ${p.accent}` }} />
            {p.label}
          </button>
        ))}
      </div>
      <div>
        <label className="eyebrow block mb-2">Your Mobile Money number</label>
        <input
          type="tel"
          value={msisdn}
          onChange={e => setMsisdn(e.target.value)}
          placeholder="+231 77 000 0000"
          data-testid="momo-msisdn-input"
          className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[#D4AF37] outline-none"
        />
      </div>
      <div className="text-xs text-zinc-500 leading-relaxed">
        On the next step you'll receive dispatcher payment details and a unique reservation code to quote in your transfer.
      </div>
    </div>
  );
}

